import React, { useState, useMemo, useCallback } from 'react';
import { useData } from '../../hooks/useData';
import { useVendorQuotes } from '../../hooks/useVendorQuotes';
import { useApp } from '../../contexts/AppContext';
import { TABLES } from '../../constants/supabaseConstants';
import { logger } from '../../utils/logger';
import { UNIT_MAP, getTopLevelChapter, formatSelectedOption } from '../../utils/boqHelpers';
import { useVendorCreate } from '../../hooks/useVendorCreate';
import { PaperPlaneTilt, Plus } from '@phosphor-icons/react';
import BOQQuoteDetail from './BOQQuoteDetail';
import QuoteRow from '../Vendors/QuoteRow';
import pageStyles from '../../styles/pages.module.css';
import tableStyles from '../../styles/tables.module.css';
import styles from './BOQQuotesTab.module.css';

// Reuse the mergeItems logic from BOQTable to create merged BOQ groups
// Each merged group has _sourceIds[] with all project_item IDs in the group
function mergeItems(items) {
  const mergeMap = new Map();
  items.forEach(item => {
    if (!item.master_item_id) {
      mergeMap.set(item.id, { ...item, _sourceIds: [item.id] });
      return;
    }
    const optionKey = JSON.stringify(item.selected_option || {});
    const mergeKey = `${item.master_item_id}__${optionKey}`;
    if (mergeMap.has(mergeKey)) {
      const existing = mergeMap.get(mergeKey);
      existing.quantity = (Number(existing.quantity) || 1) + (Number(item.quantity) || 1);
      existing._sourceIds = [...(existing._sourceIds || [existing.id]), item.id];
    } else {
      mergeMap.set(mergeKey, { ...item, _sourceIds: [item.id] });
    }
  });
  return Array.from(mergeMap.values());
}

/**
 * BOQQuotesTab — Manages vendor quotes for the BOQ table.
 * Shows list of GC vendors, allows creating quotes with all BOQ items,
 * viewing vendor pricing, and sharing links.
 */
const BOQQuotesTab = ({ projectItemRows, bbChapters, costTypes }) => {
  const { currentProject, showSuccess, showError } = useApp();
  const projectId = currentProject?.id;

  // Quote data via hook
  const {
    quotes,
    quoteItems,
    isLoading: quotesLoading,
    createQuote,
    removeQuote,
    updateQuote,
    selectQuote,
    generateShareLink,
  } = useVendorQuotes(projectId);

  // Fetch master vendors for name display and vendor picker
  const { data: masterVendors } = useData(TABLES.MASTER_VENDORS, {
    select: 'id, name, vendor_type_id, contact_person, contact_phone, contact_email',
    enabled: true,
    realtime: false,
  });

  // Vendor creation hook (reuse centralized logic)
  const { createVendor, vendorTypes, vendorTypesGrouped } = useVendorCreate();

  // Local state
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [copyFeedback, setCopyFeedback] = useState(null);
  const [showNewVendorForm, setShowNewVendorForm] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorTypeId, setNewVendorTypeId] = useState('');
  const [isCreatingVendor, setIsCreatingVendor] = useState(false);

  // Default vendor type to קבלנות ראשית when vendor types load
  React.useEffect(() => {
    if (vendorTypes && !newVendorTypeId) {
      const gcType = vendorTypes.find(vt => vt.name === 'קבלנות ראשית');
      if (gcType) setNewVendorTypeId(gcType.id);
    }
  }, [vendorTypes]);

  // Merged BOQ rows (same logic as BOQTable)
  const mergedRows = useMemo(() => mergeItems(projectItemRows || []), [projectItemRows]);

  // Build vendor lookup map
  const vendorMap = useMemo(() => {
    const map = new Map();
    masterVendors?.forEach(v => map.set(v.id, v));
    return map;
  }, [masterVendors]);

  // Vendor types reference (for category-based filtering)
  const { data: vendorTypesRef } = useData(TABLES.REFERENCE_VENDOR_TYPES, {
    select: 'id, name, category',
  });

  // Filter to only quotes from GC vendors (vendor type name = קבלנות ראשית)
  const boqQuotes = useMemo(() => {
    if (!quotes || !vendorTypesRef || !masterVendors) return [];
    // Find the קבלנות ראשית vendor type ID
    const gcType = vendorTypesRef.find(vt => vt.name === 'קבלנות ראשית');
    if (!gcType) return [];
    // Build vendor → type lookup
    const vendorTypeMap = new Map();
    masterVendors.forEach(v => vendorTypeMap.set(v.id, v.vendor_type_id));
    return quotes.filter(q => vendorTypeMap.get(q.master_vendor_id) === gcType.id);
  }, [quotes, vendorTypesRef, masterVendors]);

  // Vendors that already have a BOQ quote (to exclude from picker)
  const existingVendorIds = useMemo(
    () => new Set(boqQuotes.map(q => q.master_vendor_id)),
    [boqQuotes],
  );

  // Available vendors (not already quoted)
  const availableVendors = useMemo(
    () => (masterVendors || []).filter(v => !existingVendorIds.has(v.id)),
    [masterVendors, existingVendorIds],
  );

  /**
   * Create a new vendor and auto-select it in the dropdown
   */
  const handleCreateVendor = useCallback(async () => {
    if (!newVendorName.trim() || !newVendorTypeId) return;
    setIsCreatingVendor(true);
    try {
      const result = await createVendor({
        name: newVendorName.trim(),
        vendor_type_id: newVendorTypeId,
      });
      if (result.success) {
        showSuccess('הספק נוצר בהצלחה');
        setSelectedVendorId(result.vendor.id);
        setNewVendorName('');
        setNewVendorTypeId('');
        setShowNewVendorForm(false);
      } else {
        showError(result.error || 'שגיאה ביצירת ספק');
      }
    } catch (error) {
      logger.error('Error creating vendor', error);
      showError('שגיאה ביצירת ספק');
    } finally {
      setIsCreatingVendor(false);
    }
  }, [newVendorName, newVendorTypeId, createVendor, showSuccess, showError]);

  // Build chapter lookup map for display_data generation
  const chapterById = useMemo(() => {
    const map = new Map();
    bbChapters?.forEach(ch => map.set(ch.id, ch));
    return map;
  }, [bbChapters]);

  /**
   * Create a BOQ quote for a vendor — auto-generates one vendor_quote_item per merged BOQ row.
   * Snapshots display info into display_data so the public vendor view is self-contained.
   */
  const handleCreateQuote = useCallback(async () => {
    if (!selectedVendorId || mergedRows.length === 0) return;

    setIsCreating(true);
    try {
      // Build line items: one per merged BOQ group, with snapshotted display_data
      const lineItems = mergedRows.map(row => {
        const itemName = row.name_override || row.master_items?.item_name || '—';
        const costTypeId = row.master_items?.cost_type_id || row.details?.cost_type_id;
        const costType = costTypes?.find(ct => ct.id === costTypeId);
        const costTypeName = costType?.name || '';
        const unitLabel = costType ? UNIT_MAP[costTypeName] || costTypeName : '—';
        const chapterId = row.master_items?.bb_chapter_id || row.bb_chapter_id;
        const topChapter = getTopLevelChapter(chapterId, chapterById);
        const optionText = formatSelectedOption(row.selected_option);
        const itemNotes = row.details?.notes || '';

        return {
          project_item_ids: row._sourceIds || [row.id],
          item_type_ids: [],
          excluded_item_ids: [],
          line_price: null, // Vendor will fill this in
          line_notes: null,
          display_data: {
            item_name: itemName,
            item_notes: itemNotes,
            option_text: optionText,
            unit_label: unitLabel,
            cost_type_name: costTypeName,
            quantity: row.quantity || 1,
            chapter_code: topChapter?.code || '',
            chapter_name: topChapter?.name_he || '',
            project_name: currentProject?.name || '',
          },
        };
      });

      const quoteData = {
        master_vendor_id: selectedVendorId,
        status: 'draft',
        total_price: null,
      };

      await createQuote(quoteData, lineItems);
      setSelectedVendorId('');
    } catch (error) {
      logger.error('Error creating BOQ quote', error);
    } finally {
      setIsCreating(false);
    }
  }, [selectedVendorId, mergedRows, createQuote, costTypes, chapterById]);

  /**
   * Share link — enable sharing and copy to clipboard
   */
  const handleShare = useCallback(
    async quoteId => {
      try {
        const link = await generateShareLink(quoteId, { routePrefix: '/quote/boq/' });
        await navigator.clipboard.writeText(link);
        setCopyFeedback(quoteId);
        showSuccess('הקישור הועתק');
        setTimeout(() => setCopyFeedback(null), 2000);
      } catch (error) {
        logger.error('Error sharing quote', error);
        showError('שגיאה ביצירת קישור');
      }
    },
    [generateShareLink, showSuccess, showError],
  );

  /**
   * Delete a quote
   */
  const handleDelete = useCallback(
    async quoteId => {
      if (!window.confirm('האם למחוק הצעת מחיר זו?')) return;
      try {
        await removeQuote(quoteId);
        if (selectedQuoteId === quoteId) setSelectedQuoteId(null);
        showSuccess('הצעת המחיר נמחקה');
      } catch (error) {
        logger.error('Error deleting quote', error);
        showError('שגיאה במחיקת הצעת המחיר');
      }
    },
    [removeQuote, selectedQuoteId, showSuccess, showError],
  );

  /**
   * Change quote status to a new valid status.
   * 'selected' goes through selectQuote() to link project items + auto-reject overlaps.
   */
  const handleStatusChange = useCallback(
    async (quoteId, newStatus) => {
      try {
        if (newStatus === 'selected') {
          await selectQuote(quoteId);
          showSuccess('הצעת המחיר נבחרה בהצלחה');
        } else {
          await updateQuote({ id: quoteId, data: { status: newStatus } });
          showSuccess('הסטטוס עודכן');
        }
      } catch (error) {
        logger.error('Error updating quote status', error);
        showError('שגיאה בעדכון סטטוס');
      }
    },
    [updateQuote, selectQuote, showSuccess, showError],
  );

  // If a quote is selected, show the detail view
  if (selectedQuoteId) {
    const quote = boqQuotes.find(q => q.id === selectedQuoteId);
    if (quote) {
      return (
        <BOQQuoteDetail
          quote={quote}
          quoteItems={(quoteItems || []).filter(qi => qi.quote_id === selectedQuoteId)}
          mergedRows={mergedRows}
          vendorMap={vendorMap}
          bbChapters={bbChapters}
          costTypes={costTypes}
          onBack={() => setSelectedQuoteId(null)}
          onShare={() => handleShare(selectedQuoteId)}
        />
      );
    }
  }

  return (
    <div className={styles.container}>
      {/* Create Quote Section */}
      <div className={pageStyles.card}>
        <div className={styles.createSection}>
          <h3 className={styles.createTitle}>שלח כתב כמויות לתמחור</h3>
          <p className={styles.createDescription}>
            בחר קבלן ושלח את כתב הכמויות לקבלת הצעת מחיר. הקבלן יקבל קישור ייחודי למילוי מחירים.
          </p>
          <div className={styles.createForm}>
            <select
              className={styles.vendorSelect}
              value={selectedVendorId}
              onChange={e => setSelectedVendorId(e.target.value)}
              disabled={isCreating}
            >
              <option value="">בחר קבלן...</option>
              {availableVendors.map(v => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <button
              className={pageStyles.secondaryButton}
              onClick={() => setShowNewVendorForm(prev => !prev)}
              title="הוסף קבלן חדש"
              type="button"
            >
              <Plus size={18} />
            </button>
            <button
              className={pageStyles.primaryButton}
              onClick={handleCreateQuote}
              disabled={!selectedVendorId || isCreating || mergedRows.length === 0}
            >
              <PaperPlaneTilt size={18} />
              {isCreating ? 'יוצר...' : 'צור הצעה'}
            </button>
          </div>

          {/* Inline new vendor form */}
          {showNewVendorForm && (
            <div className={styles.newVendorForm}>
              <input
                className={styles.vendorInput}
                type="text"
                placeholder="שם הקבלן"
                value={newVendorName}
                onChange={e => setNewVendorName(e.target.value)}
                disabled={isCreatingVendor}
              />
              <select
                className={styles.vendorSelect}
                value={newVendorTypeId}
                onChange={e => setNewVendorTypeId(e.target.value)}
                disabled={isCreatingVendor}
              >
                <option value="">סוג ספק...</option>
                {vendorTypesGrouped.map(group => (
                  <optgroup key={group.category} label={group.category}>
                    {group.types.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <button
                className={pageStyles.primaryButton}
                onClick={handleCreateVendor}
                disabled={!newVendorName.trim() || !newVendorTypeId || isCreatingVendor}
              >
                {isCreatingVendor ? 'יוצר...' : 'צור קבלן'}
              </button>
            </div>
          )}
          {mergedRows.length === 0 && (
            <p className={styles.noItemsWarning}>אין פריטים בכתב הכמויות. הוסף פריטים תחילה.</p>
          )}
        </div>
      </div>

      {/* Quotes List */}
      {quotesLoading ? (
        <div className={tableStyles.loadingContainer}>
          <div className={tableStyles.spinner} />
        </div>
      ) : boqQuotes.length === 0 ? (
        <div className={pageStyles.card}>
          <div className={pageStyles.cardContent}>
            <div className={tableStyles.emptyState}>
              <p className={tableStyles.emptyTitle}>אין הצעות מחיר</p>
              <p className={tableStyles.emptyDescription}>בחר קבלן למעלה כדי ליצור הצעה חדשה</p>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.quoteList}>
          {boqQuotes.map(quote => {
            const vendor = vendorMap.get(quote.master_vendor_id);
            const qItems = (quoteItems || []).filter(qi => qi.quote_id === quote.id);
            const pricedCount = qItems.filter(qi => qi.line_price != null).length;
            const totalItems = qItems.length;
            return (
              <QuoteRow
                key={quote.id}
                quote={quote}
                vendorName={vendor?.name}
                priced={{ pricedCount, totalItems }}
                showVATTotal={true}
                copied={copyFeedback === quote.id}
                onClick={() => setSelectedQuoteId(quote.id)}
                onShare={() => handleShare(quote.id)}
                onStatusChange={newStatus => handleStatusChange(quote.id, newStatus)}
                onDelete={() => handleDelete(quote.id)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BOQQuotesTab;
